/** @jsxImportSource @emotion/react */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Button } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import { ComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { tb } from 'components/layouts/Table';
import { FileuploadBtn } from 'components/buttons/CustomButton';
import { countByItemId, generateAtchFileGrId } from 'apis/mp/MpItem';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { IconButton } from 'components/buttons/IconSVG';
import { ecoYNcodes, ItemStatCd, MpItem, MpItemAtchFileTpCd, MpItemTpCd } from 'models/mp/MpItem';
import { Code } from 'models/common/CommonCode';
import { CommonYN, commonYNcodes, ManagementMode } from 'models/common/Common';
import { EquipmentGroupCondition } from 'models/common/popup/EquipmentGroup';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { EquipmentGroupPopUp } from 'pages/common/popup/EquipmentGroupPopUp';
import FileUploadPopUp from 'pages/common/components/FileUploadPopUp';
import { FileImgInfo } from 'models/admin/FileImgInfo';
import { findFileImgList } from 'apis/file/File';
import CustomFileImageList from 'components/layouts/CustomFileImageList';
import CustomTextarea from 'components/inputs/CustomTextarea';
import EquipmentPopUp from '../../common/popup/EquipmentPopUp';
import { CustomEquipment, EquipmentLevel } from 'models/common/popup/EquipmentMaster';
import { MpResult } from 'models/mp/MpResult';
import UserMultiModal from 'components/modals/common/UserMultiModal';
import { GatingContListPopUp } from 'pages/gtng/popup/GatingContListPopUp';
import { MpMcrcList } from 'models/mp/MpMcrc';
import { getMpMcrcList } from 'apis/mp/MpMcrc';
import { findPlant, findPlantForEquipmentGroup } from 'apis/common/Plant';
import { MpEquipmentHistoryPopUp } from '../popup/MpEquipmentHistoryPopUp';
import MpMcrcPopUp from '../popup/MpMcrcPopUp';
import { MpDrwPopUp } from '../popup/MpDrwPopUp';
import { DrwList } from 'models/mp/MpDrw';
import { MpMgrChgHistoryPopUp } from '../popup/MpMgrChgHistoryPopUp';
import { open4mcc } from 'utils/ApiUtil';
import { CommonUtil } from 'utils/CommonUtil';
import { FindInputBtnSet, TextBtnSet } from 'components/layouts/TableInButton';
import { MpEquipmentPopUp } from '../popup/MpEquipmentPopUp';
import { MpDrwRetrievePopUp } from '../popup/MpDrwRetrievePopUp';
import { completeSpshRev } from 'apis/mp/MpDrw';
import useSessionStore from 'stores/useSessionStore';
import { hasRole } from '../../../utils/SessionUtil';

/**
 * @description 설비변경점 탭
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  25.10.29    jihwan89       CSR E20251027-000124 설비추가 시 설비레벨 조건 MAIN 추가(기존: Machine)
 *  25.11.10    jihwan89       설비변경점 탭에서 설비선택 시 조회조건 공정선택가능하도록 변경 (PI 요청)
 *  25.11.12    jihwan89       설비변경점 개선사항 v5.2 제작사양서 해당사항없음 선태 시 FDM 연계없이 완료 처리
 *  26.01.27    jihwan89       CSR E20251218-001101 as-is: 법인선택 후 팩토리 선택가능 / to-be 법인, 설비군 선택 후 팩토리 선택가능
 */

interface Props {
  defaultMpItem: MpItem;
  mode?: ManagementMode;
  isCopy?: boolean;
  isEditable: (column: string, fstrYn?: string) => boolean;
  onRefresh: () => void;
}

interface DisplayCondition {
  equipmentName?: string; // 설비명
  chgCrgeUserNm?: string; // 설비담당자
}

const MpItemInfoContent = (
  { defaultMpItem, mode = ManagementMode.CREATE, isCopy = false, isEditable, onRefresh }: Props,
  ref
) => {
  const { t } = useTranslation();
  const { userId } = useSessionStore();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const loadRef = useRef(false);
  const [code, setCode] = useState<any>();
  const [errors, setErrors] = useState<any>({});
  const [mpItem, setMpItem] = useState<MpItem>(defaultMpItem);
  const [displayCondition, setDisplayCondition] = useState<DisplayCondition>({});
  const [isOpenMpItemEqpListModal, setOpenMpItemEqpListModal] = useState<boolean>(false);
  const [isOpenEquipmentGroupPopUp, setOpenEequipmentGroupPopUp] = useState<boolean>(false);
  const [isOpenEquipmentModal, setOpenEquipmentModal] = useState<boolean>(false);
  const [isOpenDrwPopup, setOpenDrwPopup] = useState<boolean>(false);
  const [drwPopupCondition, setDrwPopupCondition] = useState<any>();
  const [isOpenDrwRetrievePopUp, setOpenDrwRetrievePopUp] = useState<boolean>(false);

  const [isOpenEquipmentHisModal, setOpenEquipmentHisModal] = useState<boolean>(false);
  const [isOpenMpMgrChgHistoryPopup, setOpenMpMgrChgHistoryPopup] = useState<boolean>(false);
  const [isOpenFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);
  const [fileUploadModalCondition, setFileUploadModalCondition] = useState<any>({});
  const [isOpenMcrcPopup, setOpenMcrcPopup] = useState<boolean>(false);
  const [mcrcPopupCondition, setMcrcPopupCondition] = useState<any>({});
  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);
  const [isOpenUserReadModal, setOpenUserReadModal] = useState<boolean>(false);
  const [userReadModalCondition, setUserReadModalCondition] = useState<any>({});
  const [procMgrs, setProcMgrs] = useState<MpMcrcList[]>([]);
  const [hasIfAuth, setHasIfAuth] = useState<boolean>(false);
  const isRequireDrw = useMemo(() => {
    // FastTrack인 경우: 신규개선 & 분과위심의중
    // 일반인 경우: 설비변경점 & 분과위심의중
    return (
      (mpItem?.mpItemTpCd === MpItemTpCd.MP && mpItem?.itemStatCd === ItemStatCd.McrcReview) ||
      (mpItem?.fstrYn === 'Y' &&
        mpItem?.mpItemTpCd === MpItemTpCd.NI &&
        mpItem?.itemStatCd === ItemStatCd.McrcReview)
    );
  }, [mpItem?.fstrYn, mpItem?.mpItemTpCd, mpItem?.itemStatCd]);

  const isEditableDrw = useMemo(() => {
    if (MpItemTpCd.NI === defaultMpItem?.mpItemTpCd) {
      // 신규개선 > 신규등록, 개발중, 분과위심의중
      return (
        !defaultMpItem?.itemStatCd ||
        ItemStatCd.Develop === defaultMpItem?.itemStatCd ||
        ItemStatCd.McrcReview === defaultMpItem?.itemStatCd
      );
    } else if (MpItemTpCd.MP === defaultMpItem?.mpItemTpCd) {
      // 설비변경점 > Item 등록, 4M변경중, 분과위심의중
      return (
        ItemStatCd.Regist === defaultMpItem?.itemStatCd ||
        ItemStatCd.M4Change === defaultMpItem?.itemStatCd ||
        ItemStatCd.McrcReview === defaultMpItem?.itemStatCd
      );
    }
    return false;
  }, [defaultMpItem?.fstrYn, defaultMpItem?.mpItemTpCd, defaultMpItem?.itemStatCd]);

  const procCdNm = useMemo(() => {
    const cd = (code?.prdnProcCd || []).filter((o) => o.cmnCd === mpItem?.procCd);
    return cd.length ? cd[0].cmnCdNm : '';
  }, [code?.prdnProcCd, mpItem?.procCd]);

  const procMgrNms = useMemo(
    () =>
      (procMgrs || [])
        .map((o) => o.mcrcNm)
        .filter((o) => !!o)
        .join(', '),
    [procMgrs]
  );
  const isHe = useMemo(
    () =>
      [MpItemTpCd.HE.toString(), MpItemTpCd.MPHE.toString()].includes(mpItem?.itemTpChoCd || ''),
    [mpItem?.itemTpChoCd]
  );

  const ecoYnCdNm = useMemo(() => {
    const cd = ecoYNcodes.filter((o) => o.cmnCd === mpItem?.ecoYn);
    return cd.length ? cd[0].cmnCdNm : '';
  }, [mpItem?.ecoYn]);

  useImperativeHandle(ref, () => ({
    validate: () => {
      const err = {};
      // 법인
      if (!mpItem?.tgtCopCd) {
        Object.assign(err, { tgtCopCd: true });
      }
      // 공정
      if (!mpItem?.procCd) {
        Object.assign(err, { procCd: true });
      }
      // 설비군
      if (!mpItem?.eqclId) {
        Object.assign(err, { eqclId: true });
      }
      // 개선목적 1레벨
      if (!mpItem?.iprvClsfLv1Cd) {
        Object.assign(err, { iprvClsfLv1Cd: true });
      }
      // 개선목적 2레벨
      if (!mpItem?.iprvClsfLv2Cd) {
        Object.assign(err, { iprvClsfLv2Cd: true });
      }
      // 개선목적 3레벨 (개선목적2레벨에서 EC/NC여부 판별이 안되는 경우 3레벨까지 선택)
      if (!mpItem?.iprvClsfLv3Cd && !mpItem?.ecoYn) {
        Object.assign(err, { iprvClsfLv3Cd: true });
      }
      // Fast Track여부
      if (!mpItem?.fstrYn) {
        Object.assign(err, { fstrYn: true });
      }
      // 변경전 내용
      if (!mpItem?.oldRevCtn) {
        Object.assign(err, { oldRevCtn: true });
      }
      // 변경후 내용
      if (!mpItem?.newRevCtn) {
        Object.assign(err, { newRevCtn: true });
      }

      //todo::: 대상공장, 대상설비를 선택 > 삭제 했을때 체크안해서 ...
      // // 대상공장
      // if (!mpItem?.tgtPlntCd) {
      //   Object.assign(err, { tgtPlntCd: true });
      // }
      // // 대상설비
      // if ((mpItem?.resultList || []).length < 1) {
      //   Object.assign(err, { equipmentName: true });
      // }

      setErrors(err);
      if (!Object.keys(err).filter((k) => err[k]).length) {
        return true;
      }
      return false;
    },
    validateItemStatCd: (stat) => {
      const err = {};
      const mpItemRptAtchFileCnt = mpItem?.mpItemRptAtchFileCnt || 0; // 개조/개선 Item 보고서
      const mpFstrAtchFileCnt = mpItem?.mpFstrAtchFileCnt || 0; // Fast Track 근거자료
      const scmmMintAtchFileCnt = mpItem?.scmmMintAtchFileCnt || 0; // 회의록(설비변경 심의)
      const mpHeRptAtchFileCnt = mpItem?.mpHeRptAtchFileCnt || 0; // 회의록(수평전개 심의)

      // 신규개선인 경우
      if (MpItemTpCd.NI === mpItem?.mpItemTpCd) {
        // 개발중 -> 분과위심의중
        if (ItemStatCd.McrcReview === stat) {
          // 대상공장
          if (!mpItem?.tgtPlntCd) {
            Object.assign(err, { tgtPlntCd: true });
          }
          // 대상설비
          if ((mpItem?.resultList || []).length < 1) {
            Object.assign(err, { equipmentName: true });
          }
          // 개조/개선 Item 보고서
          if (mpItemRptAtchFileCnt < 1) {
            openMessageBar({
              type: 'error',
              content: t(
                'mp.label.개조/개선 Item 보고서를 첨부해 주세요.',
                '개조/개선 Item 보고서를 첨부해 주세요.'
              ),
            });
            Object.assign(err, { mpItemRptAtchFileCnt: true });
            handleFileUpload(MpItemAtchFileTpCd.MP_ITEM_RPT);
          }
          // Fast Track인 경우
          if (mpItem?.fstrYn === 'Y') {
            // Fast Track 근거자료
            if (mpFstrAtchFileCnt < 1) {
              openMessageBar({
                type: 'error',
                content: t(
                  'mp.label.Fast track 근거자료를 첨부해 주세요.',
                  'Fast track 근거자료를 첨부해 주세요.'
                ),
              });
              Object.assign(err, { mpFstrAtchFileCnt: true });
              handleFileUpload(MpItemAtchFileTpCd.MP_FSTR);
            }
          }
        }
        // 분과위심의중 -> 개발중 (신규등록에서 개발중 상태 변경은 validate 사용)
        if (ItemStatCd.Develop === stat) {
          if (mpItemRptAtchFileCnt < 1) {
            openMessageBar({
              type: 'error',
              content: t(
                'mp.label.개조/개선 Item 보고서를 첨부해 주세요.',
                '개조/개선 Item 보고서를 첨부해 주세요.'
              ),
            });
            Object.assign(err, { mpItemRptAtchFileCnt: true });
            handleFileUpload(MpItemAtchFileTpCd.MP_ITEM_RPT);
          }
          // 재검토 사유 (상태변경 함수에서 사유받아서 확인)
        }
        // 분과위심의중 -> Item 등록 (설비변경점 or 설비변경점/수평전개)
        if (ItemStatCd.Regist === stat) {
          // todo::: 분과위원이 있는지 확인
          if (procMgrs.length < 1) {
            openMessageBar({
              type: 'error',
              content: t(
                'mp.label.지정된 분과위원이 없습니다. 분류, 공정별 분과위원이 지정되어야 합니다.',

                '지정된 분과위원이 없습니다. 분류, 공정별 분과위원이 지정되어야 합니다.'
              ),
            });
            Object.assign(err, { procMgrs: true });
          }
          if (mpItemRptAtchFileCnt < 1) {
            openMessageBar({
              type: 'error',
              content: t(
                'mp.label.개조/개선 Item 보고서를 첨부해 주세요.',
                '개조/개선 Item 보고서를 첨부해 주세요.'
              ),
            });
            Object.assign(err, { mpItemRptAtchFileCnt: true });
            handleFileUpload(MpItemAtchFileTpCd.MP_ITEM_RPT);
          }
          // 분과위원회 회의록
          if (scmmMintAtchFileCnt < 1) {
            openMessageBar({
              type: 'error',
              content: t(
                'mp.label.회의록(설비변경 심의)을 첨부해 주세요.',
                '회의록(설비변경 심의)을 첨부해 주세요.'
              ),
            });
            Object.assign(err, { scmmMintAtchFileCnt: true });
            handleFileUpload(MpItemAtchFileTpCd.SCMM_MINT);
          }
          // Fast Track인 경우
          if (mpItem?.fstrYn === 'Y') {
            // 사양서 담당자
            if (!mpItem?.chgCrgeUserId) {
              Object.assign(err, { chgCrgeUserId: true });
            }
            // 제작사양서 개정대상
            if ((mpItem?.mpDrwList || []).length < 1) {
              Object.assign(err, { drwName: true });
            }
            // 신규개선 분과심의중 >  심의완료 할때도 근거자료 체크 해야함.
            if (mpFstrAtchFileCnt < 1) {
              openMessageBar({
                type: 'error',
                content: t(
                  'mp.label.Fast track 근거자료를 첨부해 주세요.',
                  'Fast track 근거자료를 첨부해 주세요.'
                ),
              });
              Object.assign(err, { mpFstrAtchFileCnt: true });
              handleFileUpload(MpItemAtchFileTpCd.MP_FSTR);
            }
          }
        }
      }
      // 설비변경점
      else if (MpItemTpCd.MP === mpItem?.mpItemTpCd) {
        // 분과위심의중 -> 심의완료 (Item등록 or 수평전개보류)
        if (ItemStatCd.Regist === stat || ItemStatCd.HeHold === stat) {
          if (mpItemRptAtchFileCnt < 1) {
            openMessageBar({
              type: 'error',
              content: t(
                'mp.label.개조/개선 Item 보고서를 첨부해 주세요.',
                '개조/개선 Item 보고서를 첨부해 주세요.'
              ),
            });
            Object.assign(err, { mpItemRptAtchFileCnt: true });
            handleFileUpload(MpItemAtchFileTpCd.MP_ITEM_RPT);
          }
          if (scmmMintAtchFileCnt < 1) {
            openMessageBar({
              type: 'error',
              content: t(
                'mp.label.회의록(설비변경 심의)을 첨부해 주세요.',
                '회의록(설비변경 심의)을 첨부해 주세요.'
              ),
            });
            Object.assign(err, { scmmMintAtchFileCnt: true });
            handleFileUpload(MpItemAtchFileTpCd.SCMM_MINT);
          }
          // 재검토 사유 (상태변경 함수에서 사유받아서 확인)
          // 수평전개의사결정 회의록
          if (mpHeRptAtchFileCnt < 1) {
            openMessageBar({
              type: 'error',
              content: t(
                'mp.label.회의록(수평전개 심의)을 첨부해 주세요.',
                '회의록(수평전개 심의)을 첨부해 주세요.'
              ),
            });
            Object.assign(err, { mpHeRptAtchFileCnt: true });
            handleFileUpload(MpItemAtchFileTpCd.MP_HE_RPT);
          }

          // 사양서 담당자
          if (!mpItem?.chgCrgeUserId) {
            Object.assign(err, { chgCrgeUserId: true });
          }
          // 제작사양서 개정대상
          if ((mpItem?.mpDrwList || []).length < 1) {
            Object.assign(err, { drwName: true });
          }
          // 수평전개 보류인 경우
          if (ItemStatCd.HeHold === stat) {
            // 수평전개 보류 (상태변경 함수에서 사유받아서 확인)
          }
        }
      }
      setErrors(err);
      if (!Object.keys(err).filter((k) => err[k]).length) {
        return true;
      }

      return false;
    },
    getMpItem: () => {
      return mpItem;
    },
    handleItemChange: (key, value) => {
      handleItemChange(key, value);
    },
    refresh: (mpItem) => {
      setMpItem(mpItem);
      changeSearchConditionByIprvClsfLv1Cd(mpItem?.iprvClsfLv1Cd, true);
      changeSearchConditionByIprvClsfLv2Cd(mpItem?.iprvClsfLv2Cd, true);
    },
  }));

  useEffect(() => {
    setMpItem(defaultMpItem);
    setDisplayCondition({
      chgCrgeUserNm: defaultMpItem?.chgCrgeUserNm || '',
    });
    loadRef.current = false;
    getCommonCodesForGrid();
  }, [defaultMpItem?.itemId]);

  useEffect(() => {
    setMpItem((prev) => ({
      ...prev,
      atchFileCnt: defaultMpItem.atchFileCnt,
      specSheetAtchFileCnt: defaultMpItem.specSheetAtchFileCnt,
    }));
  }, [defaultMpItem.atchFileCnt, defaultMpItem.specSheetAtchFileCnt]);

  useEffect(() => {
    changeSearchConditionByMpClsfCd(defaultMpItem?.mpClsfCd, defaultMpItem?.eqclId, true);
  }, [defaultMpItem?.mpClsfCd]);

  // useEffect(() => {
  //   if (mpItem?.tgtCopCd) {
  //     findPlant({
  //       copCd: mpItem?.tgtCopCd,
  //     }).then((result) => {
  //       setCode((prev) => ({
  //         ...prev,
  //         plant: (result || []).reduce(
  //           (acc, cur) => [...acc, { cmnCd: cur.prdnPlntCd, cmnCdNm: cur.prdnPlntNm }],
  //           [] as Code[]
  //         ),
  //       }));
  //     });
  //   } else {
  //     setCode((prev) => ({
  //       ...prev,
  //       plant: [],
  //     }));
  //   }
  // }, [mpItem?.tgtCopCd]);

  useEffect(() => {
    // [26.01.27] CSR E20251218-001101 as-is: 법인선택 후 팩토리 선택가능 / to-be 법인, 설비군 선택 후 팩토리 선택가능
    if (mpItem?.tgtCopCd && mpItem?.eqclId) {
      findPlantForEquipmentGroup({
        copCd: mpItem?.tgtCopCd,
        eqclId: mpItem?.eqclId,
      }).then((result) => {
        setCode((prev) => ({
          ...prev,
          plant: (result || []).reduce(
            (acc, cur) => [...acc, { cmnCd: cur.prdnPlntCd, cmnCdNm: cur.prdnPlntNm }],
            [] as Code[]
          ),
        }));
      });
    } else {
      setCode((prev) => ({
        ...prev,
        plant: [],
      }));
    }
  }, [mpItem?.tgtCopCd, mpItem?.eqclId]);

  useEffect(() => {
    const appEnv = `${process.env.REACT_APP_NODE_ENV}` || '';
    const testEnv = ['local', 'dev', 'stg'];
    setHasIfAuth(hasRole('ADM') && testEnv.includes(appEnv));
  }, []);

  useEffect(() => {
    if (defaultMpItem?.mpClsfCd && mpItem?.procCd) {
      getMpMcrcList({
        mpClsfType: defaultMpItem?.mpClsfCd,
        prdnProcCd: mpItem?.procCd,
      }).then((result: MpMcrcList[]) => {
        setProcMgrs(result);
      });
    } else {
      setProcMgrs([]);
    }
  }, [defaultMpItem?.mpClsfCd, mpItem?.procCd]);

  useEffect(() => {
    if (loadRef.current) {
      init();
    }
  }, [loadRef.current]);

  const init = () => {
    // if (ManagementMode.MODIFY === mode || isCopy) {
    if ([ManagementMode.CREATE, ManagementMode.MODIFY].includes(mode) || isCopy) {
      setDisplayCondition({
        ...displayCondition,
        chgCrgeUserNm: defaultMpItem?.chgCrgeUserNm,
      });
      changeSearchConditionByItemTpChoCd(defaultMpItem?.itemTpChoCd, true);
      changeSearchConditionByMpClsfCd(defaultMpItem?.mpClsfCd, defaultMpItem?.eqclId, true);
      changeSearchConditionByIprvClsfLv1Cd(defaultMpItem?.iprvClsfLv1Cd, true);
      changeSearchConditionByIprvClsfLv2Cd(defaultMpItem?.iprvClsfLv2Cd, true);
    }
    getPreviewImage();
  };

  const [groupsTitle, setGroupsTitle] = useState<any>([]);
  const [groupsData, setGroupsData] = useState<{ [key: string]: FileImgInfo[] }>({});

  const getPreviewImage = () => {
    mpItem?.atchFileGrId &&
      findFileImgList(mpItem?.atchFileGrId).then((data) => {
        if (data) {
          const groupedData = data.reduce((groups, item) => {
            const key = JSON.stringify({ groupId: item.atchFileGrId, id: item.atchFileId });
            if (!groups[key]) {
              groups[key] = [];
            }
            groups[key].push(item);
            return groups;
          }, {});

          const groupedTitleData = data.reduce((groups, item) => {
            const key = JSON.stringify({ groupId: item.atchFileGrId, id: item.atchFileId });
            if (!groups[key]) {
              groups[key] = [];
            } else {
              return groups;
            }
            groups[key].push({ key: key, atchFileNm: item.atchFileNm });
            return groups;
          }, {});

          const resultGroupTitle = Object.values(groupedTitleData);

          setGroupsTitle(resultGroupTitle);
          setGroupsData(groupedData);
        }
      });
  };

  /**
   * MP분류 변경에 따른 검색조건 값 설정 변경
   *
   * 재설정 항목
   * 1. 공정
   * 2. 개선목적 Level1
   * 3. 작업구분
   * 4. 설비분류체계
   */
  const changeSearchConditionByMpClsfCd = async (
    mpClsfCd?: string,
    eqclId?: string,
    isFirst = false
  ) => {
    let optValCtn1 = '';
    if (mpClsfCd) {
      const c = (code?.mpClsfCd || []).filter((o) => (mpClsfCd || '').includes(o.cmnCd));
      if (c.length) {
        optValCtn1 = c[0].optValCtn1;
      }
    }

    if (!isFirst) {
      setMpItem((prev) => ({
        ...prev,
        procCd: '', // 공정
        iprvClsfLv1Cd: '', // 개선목적 Level1
      }));
    }

    let prdnProcCd = [] as Code[];
    let mpImprTypeLvl1 = [] as Code[];

    // MP 분류에 따른 검색조건 코드 조회
    if (optValCtn1) {
      // 1. 공정
      prdnProcCd = await getCommonCodeNamesCondition({
        cmnGrCd: 'PRDN_PROC_CD',
        optValCtn2: 'Y',
        optValCtn6: optValCtn1,
      });

      // 2. 개선목적 Level1 (iprvClsfLv1Cd)
      mpImprTypeLvl1 = await getCommonCodeNamesCondition({
        cmnGrCd: 'MP_IMPR_TYPE_LVL1',
        // optValCtn1: optValCtn1, // [25.07.30] 개선목적 Level1 옵션1값 조건 사용안함 (ex. CMN, UT 등)
      });
    }

    setCode((prev) => ({
      ...prev,
      mpImprTypeLvl1: mpImprTypeLvl1,
      prdnProcCd: prdnProcCd,
    }));
  };

  /**
   * 개선목적 Level1 변경에 따른 검색조건 값 설정 변경
   *
   * 재설정 항목
   * 1. 개선목적 Level2
   */
  const changeSearchConditionByIprvClsfLv1Cd = async (iprvClsfLv1Cd?: string, isFirst = false) => {
    if (!isFirst) {
      setMpItem((prev) => ({
        ...prev,
        iprvClsfLv2Cd: '', // 개선목적 Level2
        iprvClsfLv3Cd: '', // 개선목적 Level3
        ecoYn: '', // eco여부 (ec/nc)
      }));
    }

    let mpImprTypeLvl2 = [] as Code[];
    // 개선목적 Level1에 따른 검색조건 코드 조회
    if (iprvClsfLv1Cd) {
      // 1. 개선목적 Level2 (iprvClsfLv2Cd)
      mpImprTypeLvl2 = await getCommonCodeNamesCondition({
        cmnGrCd: 'MP_IMPR_TYPE_LVL2',
        optValCtn1: iprvClsfLv1Cd,
      });
    }
    setCode((prev) => ({
      ...prev,
      mpImprTypeLvl2: mpImprTypeLvl2,
    }));
  };

  /**
   * 개선목적 Level2 변경에 따른 검색조건 값 설정 변경
   *
   * 재설정 항목
   * 1. 개선목적 Level3
   */
  const changeSearchConditionByIprvClsfLv2Cd = async (iprvClsfLv2Cd?: string, isFirst = false) => {
    if (!isFirst) {
      setMpItem((prev) => ({
        ...prev,
        iprvClsfLv3Cd: '', // 개선목적 Level3
        ecoYn: '', // eco여부 (ec/nc)
      }));
    }

    let mpImprTypeLvl3 = [] as Code[];
    // 개선목적 Level2에 따른 검색조건 코드 조회
    if (iprvClsfLv2Cd) {
      // 1. 개선목적 Level3 (iprvClsfLv3Cd)
      mpImprTypeLvl3 = await getCommonCodeNamesCondition({
        cmnGrCd: 'MP_IMPR_TYPE_LVL3',
        optValCtn2: iprvClsfLv2Cd,
      });

      /*
      // [25.07.08] MP_IMPR_TYPE_LVL2 옵션2 en/nc여부 삭제
      const cd = (code?.mpImprTypeLvl2 || []).filter((o) => o.cmnCd === iprvClsfLv2Cd);
      if (cd.length && cd[0]?.optValCtn2) {
        const code = ecoYNcodes.filter((o) => o.cmnCdNm === cd[0]?.optValCtn2 || '');
        setMpItem((prev) => ({ ...prev, ecoYn: code.length > 0 ? code[0].cmnCd : '' }));
      }
      */
    }
    setCode((prev) => ({
      ...prev,
      mpImprTypeLvl3: mpImprTypeLvl3,
    }));
  };

  /**
   * MP유형 변경에 따른 검색조건 값 설정 변경
   *
   * 재설정 항목
   * 1. Item 상태
   */
  const changeSearchConditionByItemTpChoCd = async (itemTpChoCd?: string, isFirst = false) => {
    if (!isFirst) {
      setMpItem((prev) => ({
        ...prev,
        itemStatCd: '', // Item 상태
      }));
    }

    let mpItemStatus = [] as Code[];
    // MP 유형에 따른 검색조건 코드 조회
    if (itemTpChoCd) {
      // 1. Item 상태
      mpItemStatus = await getCommonCodeNamesCondition({
        cmnGrCd: 'MP_ITEM_STAT_CD',
        optValCtn1: itemTpChoCd,
      });
    }
    setCode((prev) => ({
      ...prev,
      mpItemStatus: mpItemStatus,
    }));

    // 신규 등록일 경우, MP유형에 따라 상태값 자동변경
    if (ManagementMode.CREATE === mode) {
      let itemStatCd = '';
      switch (itemTpChoCd) {
        case 'HE':
          itemStatCd = 'HE01';
          break;
        case 'MP':
          itemStatCd = 'MP01';
          break;
        case 'MPHE':
          itemStatCd = 'MPHE01';
          break;
        case 'NI':
          itemStatCd = 'NI01';
          break;
      }

      setMpItem((prev) => ({
        ...prev,
        itemStatCd: itemStatCd, // Item 상태
      }));
    }
  };

  const getCommonCodesForGrid = async () => {
    const mpClsfCd: Code[] = await getCommonCodeNames('MP_CLSF_CD'); // MP분류
    const mpItemTpCd: Code[] = await getCommonCodeNames('MP_ITEM_TP_CD'); // MP유형
    const elmCopCd: Code[] = await getCommonCodeNames('ELM_COP_CD'); // 법인코드

    setCode((prev) => ({
      ...prev,
      mpClsfCd: mpClsfCd,
      mpItemTpCd: mpItemTpCd,
      elmCopCd: elmCopCd,
    }));

    if (!loadRef.current) {
      loadRef.current = true;
    }
  };

  const handleItemChange = (name: string, value: string | string[]) => {
    setMpItem((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const handleFileUpload = async (atchFileTpCd: string) => {
    let atchFileGrId = mpItem?.atchFileGrId;
    if (isEditable('attach')) {
      if (_.isEmpty(atchFileGrId)) {
        atchFileGrId = await generateAtchFileGrId(mpItem?.itemId || '');
        if (atchFileGrId) {
          setMpItem((prev) => ({
            ...prev,
            atchFileGrId: atchFileGrId,
          }));
        } else {
          openMessageBar({
            type: 'error',
            content:
              t(
                'com.label.첨부파일그룹ID 생성에 실패했습니다.',
                '첨부파일그룹ID 생성에 실패했습니다.'
              ) +
              <br /> +
              t('com.label.새로고침 후 다시 시도해 주세요.', '새로고침 후 다시 시도해 주세요.'),
          });
          return;
        }
      }
    } else {
      // 수정불가능한 상태인데 그룹ID가 없는 경우 미노출
      if (!atchFileGrId) {
        return;
      }
      let key = '';
      switch (atchFileTpCd) {
        case MpItemAtchFileTpCd.MP_ITEM:
          key = 'atchFileCnt';
          break;
        case MpItemAtchFileTpCd.MP_MINUTES:
          key = 'minuteAtchFileCnt';
          break;
        case MpItemAtchFileTpCd.SPEC_SHEET:
          key = 'specSheetAtchFileCnt';
          break;
        case MpItemAtchFileTpCd.MP_ITEM_RPT:
          key = 'mpItemRptAtchFileCnt';
          break;
        case MpItemAtchFileTpCd.VAID_EVL_RPT:
          key = 'vaidEvlRptAtchFileCnt';
          break;
        case MpItemAtchFileTpCd.SCMM_MINT:
          key = 'scmmMintAtchFileCnt';
          break;
        case MpItemAtchFileTpCd.MP_FSTR:
          key = 'mpFstrAtchFileCnt';
          break;
        case MpItemAtchFileTpCd.MP_HE_RPT:
          key = 'mpHeRptAtchFileCnt';
          break;
      }
      // 수정불가능한 상태인데 첨부파일이 없는 경우 미노출
      if (key && mpItem[key] < 1) {
        return;
      }
    }

    setFileUploadModalCondition({
      atchFileGrId: atchFileGrId,
      atchFileTpCd: atchFileTpCd,
      tableName: 'tb_elm_mp_item_m',
      bizName: 'mp',
    });
    setOpenFileUploadModal(true);
  };

  const handleDisplayConditionChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const name = e.target.name;
    setDisplayCondition((prev) => ({
      ...prev,
      [name]: e.target.value,
    }));

    setMpItem((prev) => ({
      ...prev,
      [name.replace('Nm', 'Id')]: '',
    }));
  };

  const handleOpenDrwModal = () => {
    if (!isEditableDrw) {
      setOpenDrwRetrievePopUp(true);
      return;
    }
    setDrwPopupCondition({
      drwIds: (mpItem?.mpDrwList || []).map((o) => o.drwId || ''),
    });
    setOpenDrwPopup(true);
  };

  const handleOpenEquipmentModal = () => {
    if (!isEditable('equipmentName')) {
      setOpenMpItemEqpListModal(true);
      return;
    }
    if (!mpItem?.tgtCopCd) {
      openMessageBar({
        type: 'error',
        content: String(t('mp.label.법인을 선택해 주세요.', '법인을 선택해 주세요.')),
      });
      return;
    }
    if (!mpItem?.procCd) {
      openMessageBar({
        type: 'error',
        content: String(t('mp.label.공정을 선택해 주세요.', '공정을 선택해 주세요.')),
      });
      return;
    }
    if (!mpItem?.eqclId) {
      openMessageBar({
        type: 'error',
        content: String(t('mp.label.설비군을 선택해 주세요.', '설비군을 선택해 주세요.')),
      });
      return;
    }
    if (!mpItem?.tgtPlntCd) {
      openMessageBar({
        type: 'error',
        content: String(
          t('mp.label.대상 Factory를 선택해 주세요.', '대상 Factory를 선택해 주세요.')
        ),
      });
      return;
    }
    setOpenEquipmentModal(true);
  };

  const handleOpenEquipmentGroupModal = () => {
    if (!mpItem?.tgtCopCd) {
      openMessageBar({
        type: 'error',
        content: String(t('mp.label.법인을 선택해 주세요.', '법인을 선택해 주세요.')),
      });
      return;
    }
    setOpenEequipmentGroupPopUp(true);
  };

  /**
   * Item 정보가 존재하는지 확인 후 동작 처리
   *
   * 대상법인, 표준사양서, 첨부파일은 item 저장 후 설정 가능
   */
  const handleOperationAfterSave = () => {
    return new Promise<void>((resolve) => {
      countByItemId(defaultMpItem?.itemId || '').then((result) => {
        if (result > 0) {
          resolve();
        } else if (result === 0) {
          openMessageBar({
            type: 'error',
            content: String(t('mp.msg.저장 후 설정해 주세요.', '저장 후 설정해 주세요.')),
          });
        } else {
          openMessageBar({
            type: 'error',
            content: String(
              t('mp.msg.등록 확인을 실패했습니다.', '등록 확인을 실패했습니다.') +
                <br /> +
                t('com.label.잠시 후 다시 시도해 주세요.', '잠시 후 다시 시도해 주세요.')
            ),
          });
        }
      });
    });
  };

  const handleOnclick = () => {
    if (code?.prdnProcCd.length < 1) {
      openMessageBar({
        type: 'error',
        content: String(t('mp.msg.분류를 선택해 주세요.', '분류를 선택해 주세요.')),
      });
      return false;
    }
  };

  return (
    <>
      <TableContainer css={tb.table}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.법인', '법인'))}</span>
              </TableCell>
              <TableCell colSpan={2}>
                {isEditable('tgtCopCd') ? (
                  <ComboBox
                    placeholder={String(t('com.label.선택', '선택'))}
                    options={code?.elmCopCd || []}
                    defaultValue={mpItem?.tgtCopCd}
                    onChange={(value) => {
                      handleItemChange('tgtCopCd', value);
                      setMpItem((prev) => ({ ...prev, tgtPlntCd: '', resultList: [], eqclId: '' }));
                      // changeSearchConditionByItemTpChoCd(value);
                    }}
                    isError={errors?.tgtCopCd}
                    msgError={String(t('mp.msg.법인을 선택해 주세요.', '법인을 선택해 주세요.'))}
                  />
                ) : (
                  mpItem?.tgtCopCd
                )}
              </TableCell>
              <TableCell>
                <span className="dot">{String(t('mp.label.공정', '공정'))}</span>
              </TableCell>
              <TableCell colSpan={2}>
                {isEditable('procCd') ? (
                  <ComboBox
                    placeholder={String(t('com.label.선택', '선택'))}
                    options={code?.prdnProcCd}
                    defaultValue={mpItem?.procCd}
                    onChange={(value) => {
                      handleItemChange('procCd', value);
                      setMpItem((prev) => ({ ...prev, resultList: [] }));
                    }}
                    isError={errors?.procCd}
                    msgError={String(t('mp.msg.공정을 선택해 주세요.', '공정을 선택해 주세요.'))}
                    onClick={handleOnclick}
                  />
                ) : (
                  procCdNm || mpItem?.procCd
                )}
              </TableCell>
              <TableCell>
                <span className="dot">{String(t('mp.label.설비군', '설비군'))}</span>
              </TableCell>
              <TableCell colSpan={2}>
                {isEditable('eqclId') ? (
                  <CustomInputWithSearch
                    className="find"
                    name="eqclId"
                    placeholder={String(
                      t('mp.label.설비군을 선택해 주세요.', '설비군을 선택해 주세요.')
                    )}
                    value={mpItem?.eqclId}
                    onChange={(e) => {
                      handleItemChange('eqclId', e.target.value);
                      setMpItem((prev) => ({ ...prev, resultList: [] }));
                    }}
                    onSearchClick={handleOpenEquipmentGroupModal}
                    isError={errors?.eqclId}
                    msgError={String(
                      t('mp.label.설비군을 선택해 주세요.', '설비군을 선택해 주세요.')
                    )}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        setOpenEequipmentGroupPopUp(true);
                      }
                    }}
                  />
                ) : (
                  mpItem?.eqclId
                )}
              </TableCell>
              <TableCell>
                <span>{String(t('mp.label.원천시스템', '원천시스템'))}</span>
              </TableCell>
              <TableCell colSpan={2}>{mpItem?.rawSysNm}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.개선목적', '개선목적'))}</span>
              </TableCell>
              <TableCell className="duplication" colSpan={6}>
                <div>
                  <ComboBox
                    placeholder={String(
                      t('mp.msg.개선목적 LV1을 선택해 주세요.', '개선목적 LV1을 선택해 주세요.')
                    )}
                    options={code?.mpImprTypeLvl1}
                    defaultValue={mpItem?.iprvClsfLv1Cd}
                    onChange={(value) => {
                      handleItemChange('iprvClsfLv1Cd', value);
                      changeSearchConditionByIprvClsfLv1Cd(value);
                    }}
                    readOnly={!isEditable('iprvClsfLv1Cd')}
                    isError={errors?.iprvClsfLv1Cd}
                    msgError={String(
                      t('mp.msg.개선목적 LV1을 선택해 주세요.', '개선목적 LV1을 선택해 주세요.')
                    )}
                  />
                  <ComboBox
                    placeholder={String(
                      t('mp.msg.개선목적 LV2을 선택해 주세요.', '개선목적 LV2을 선택해 주세요.')
                    )}
                    options={code?.mpImprTypeLvl2}
                    defaultValue={mpItem?.iprvClsfLv2Cd}
                    onChange={(value) => {
                      handleItemChange('iprvClsfLv2Cd', value);
                      changeSearchConditionByIprvClsfLv2Cd(value);
                    }}
                    readOnly={!isEditable('iprvClsfLv2Cd')}
                    isError={errors?.iprvClsfLv2Cd}
                    msgError={String(
                      t('mp.msg.개선목적 LV2을 선택해 주세요.', '개선목적 LV2을 선택해 주세요.')
                    )}
                  />
                  <ComboBox
                    placeholder={String(
                      t('mp.msg.개선목적 LV3을 선택해 주세요.', '개선목적 LV3을 선택해 주세요.')
                    )}
                    options={code?.mpImprTypeLvl3}
                    defaultValue={mpItem?.iprvClsfLv3Cd}
                    onChange={(value) => {
                      handleItemChange('iprvClsfLv3Cd', value);
                      const cd = (code?.mpImprTypeLvl3 || []).filter((o) => o.cmnCd === value);
                      if (cd.length) {
                        const code = ecoYNcodes.filter(
                          (o) => o.cmnCdNm === cd[0]?.optValCtn3 || ''
                        );
                        setMpItem((prev) => ({
                          ...prev,
                          ecoYn: code.length > 0 ? code[0].cmnCd : '',
                        }));
                      }
                    }}
                    readOnly={!isEditable('iprvClsfLv3Cd')}
                    isError={errors?.iprvClsfLv3Cd}
                    msgError={String(
                      t('mp.msg.개선목적 LV3을 선택해 주세요.', '개선목적 LV3을 선택해 주세요.')
                    )}
                  />
                </div>
              </TableCell>
              <TableCell>
                <span>{String(t('mp.label.NC/EC', 'NC/EC'))}</span>
              </TableCell>
              <TableCell>{mpItem?.ecoYn && (ecoYnCdNm || mpItem?.ecoYn)}</TableCell>
              <TableCell>
                <span>{String(t('mp.label.Fast Track 여부', 'Fast Track 여부'))}</span>
              </TableCell>
              <TableCell colSpan={2}>
                {isEditable('fstrYn') ? (
                  <ComboBox
                    options={commonYNcodes}
                    defaultValue={mpItem?.fstrYn}
                    onChange={(value) => {
                      setMpItem((prev) => ({ ...prev, fstrYn: value }));
                    }}
                  />
                ) : (
                  mpItem?.fstrYn
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.대상 Factory', '대상 Factory'))}</span>
              </TableCell>
              <TableCell colSpan={2}>
                {isEditable('tgtPlntCd') ? (
                  <ComboBox
                    placeholder={String(t('com.label.선택', '선택'))}
                    options={code?.plant || []}
                    defaultValue={mpItem?.tgtPlntCd}
                    onChange={(value) => {
                      handleItemChange('tgtPlntCd', value);
                      setMpItem((prev) => ({ ...prev, resultList: [] }));
                    }}
                    isError={errors?.tgtPlntCd}
                    msgError={String(
                      t('mp.msg.Factory를 선택해 주세요.', 'Factory를 선택해 주세요.')
                    )}
                  />
                ) : (
                  mpItem?.tgtPlntCdNm
                )}
              </TableCell>
              <TableCell>
                <span className="dot">{String(t('mp.label.대상 설비', '대상 설비'))}</span>
              </TableCell>
              <TableCell colSpan={8}>
                <FindInputBtnSet>
                  <CustomInputWithSearch
                    className="find"
                    name="equipmentName"
                    placeholder={String(
                      t('mp.msg.대상 설비를 선택해 주세요.', '대상 설비를 선택해 주세요.')
                    )}
                    value={(mpItem?.resultList || [])
                      .reduce((acc, cur) => [...acc, cur?.equipmentName || ''], [] as string[])
                      .filter((o) => !!o)
                      .join(', ')}
                    onSearchClick={handleOpenEquipmentModal}
                    readOnly={!isEditable('equipmentName')}
                    isError={errors?.equipmentName}
                    msgError={String(
                      t('mp.msg.대상 설비를 선택해 주세요.', '대상 설비를 선택해 주세요.')
                    )}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        handleOpenEquipmentModal();
                      }
                    }}
                  />
                  <Button
                    css={IconButton.button}
                    className="docu"
                    onClick={() => setOpenEquipmentHisModal(true)}
                  />
                </FindInputBtnSet>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{String(t('mp.label.변경전 내용', '변경전 내용'))}</span>
              </TableCell>
              <TableCell colSpan={4}>
                <CustomTextarea
                  name="oldRevCtn"
                  placeholder={String(
                    t('mp.label.변경전 내용을 입력해 주세요.', '변경전 내용을 입력해 주세요.')
                  )}
                  value={mpItem?.oldRevCtn}
                  readOnly={!isEditable('oldRevCtn')}
                  isError={errors?.oldRevCtn}
                  msgError={String(
                    t('mp.label.변경전 내용을 입력해 주세요.', '변경전 내용을 입력해 주세요.')
                  )}
                  onChange={(event: any) => {
                    const value = event?.target.value;
                    if (value.length <= 1000) {
                      handleItemChange(event.target.name, value);
                    } else {
                      openMessageBar({
                        type: 'error',
                        content: t(
                          'mp.label.변경전 내용은 1000자를 넘을 수 없습니다.',
                          '변경전 내용은 1000자를 넘을 수 없습니다.'
                        ),
                      });
                    }
                  }}
                  wrapStyle={{ paddingBottom: '0px', height: '65px' }}
                />
              </TableCell>
              <TableCell>
                <span className="dot">{String(t('mp.label.변경후 내용', '변경후 내용'))}</span>
              </TableCell>
              <TableCell colSpan={6}>
                <CustomTextarea
                  name="newRevCtn"
                  placeholder={String(
                    t('mp.label.변경후 내용을 입력해 주세요.', '변경후 내용을 입력해 주세요.')
                  )}
                  value={mpItem?.newRevCtn}
                  readOnly={!isEditable('newRevCtn')}
                  isError={errors?.newRevCtn}
                  msgError={String(
                    t('mp.label.변경후 내용을 입력해 주세요.', '변경후 내용을 입력해 주세요.')
                  )}
                  onChange={(event: any) => {
                    const value = event?.target.value;
                    if (value.length <= 1000) {
                      handleItemChange(event.target.name, value);
                    } else {
                      openMessageBar({
                        type: 'error',
                        content: t(
                          'mp.label.변경후 내용은 1000자를 넘을 수 없습니다.',
                          '변경후 내용은 1000자를 넘을 수 없습니다.'
                        ),
                      });
                    }
                  }}
                  wrapStyle={{ paddingBottom: '0px', height: '65px' }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={12} style={{ textAlign: 'center' }}>
                <span>{String(t('com.label.첨부', '첨부'))}</span>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span>{String(t('mp.label.일반첨부', '일반첨부'))}</span>
              </TableCell>
              <TableCell>
                <FileuploadBtn
                  onClick={() =>
                    handleOperationAfterSave().then(() => {
                      handleFileUpload(MpItemAtchFileTpCd.MP_ITEM);
                    })
                  }
                >
                  <span>{(mpItem?.atchFileCnt || 0).toString()}</span>
                </FileuploadBtn>
              </TableCell>
              <TableCell>
                <span>{String(t('mp.label.개조/개선 Item 보고서', '개조/개선 Item 보고서'))}</span>
              </TableCell>
              <TableCell>
                <FileuploadBtn
                  onClick={() =>
                    handleOperationAfterSave().then(() => {
                      handleFileUpload(MpItemAtchFileTpCd.MP_ITEM_RPT);
                    })
                  }
                >
                  <span>{(mpItem?.mpItemRptAtchFileCnt || 0).toString()}</span>
                </FileuploadBtn>
              </TableCell>
              <TableCell>
                <span>{String(t('mp.label.유효성 평가 보고서', '유효성 평가 보고서'))}</span>
              </TableCell>
              <TableCell>
                <FileuploadBtn
                  onClick={() =>
                    handleOperationAfterSave().then(() => {
                      handleFileUpload(MpItemAtchFileTpCd.VAID_EVL_RPT);
                    })
                  }
                >
                  <span>{(mpItem?.vaidEvlRptAtchFileCnt || 0).toString()}</span>
                </FileuploadBtn>
              </TableCell>
              <TableCell>
                <span>{String(t('mp.label.회의록(설비변경 심의)', '회의록(설비변경 심의)'))}</span>
              </TableCell>
              <TableCell>
                <FileuploadBtn
                  onClick={() =>
                    handleOperationAfterSave().then(() => {
                      handleFileUpload(MpItemAtchFileTpCd.SCMM_MINT);
                    })
                  }
                >
                  <span>{(mpItem?.scmmMintAtchFileCnt || 0).toString()}</span>
                </FileuploadBtn>
              </TableCell>
              <TableCell>
                <span className={mpItem?.fstrYn === 'Y' ? 'dot' : ''}>
                  {String(t('mp.label.Fast Track 근거자료', 'Fast Track 근거자료'))}
                </span>
              </TableCell>
              <TableCell>
                <FileuploadBtn
                  onClick={() => {
                    handleOperationAfterSave().then(() => {
                      handleFileUpload(MpItemAtchFileTpCd.MP_FSTR);
                    });
                  }}
                >
                  <span>{(mpItem?.mpFstrAtchFileCnt || 0).toString()}</span>
                </FileuploadBtn>
              </TableCell>
              <TableCell>
                <span>{String(t('mp.label.회의록(수평전개 심의)', '회의록(수평전개 심의)'))}</span>
              </TableCell>
              <TableCell>
                <FileuploadBtn
                  onClick={() => {
                    handleOperationAfterSave().then(() => {
                      handleFileUpload(MpItemAtchFileTpCd.MP_HE_RPT);
                    });
                  }}
                >
                  <span>{(mpItem?.mpHeRptAtchFileCnt || 0).toString()}</span>
                </FileuploadBtn>
              </TableCell>
              {/*
              <TableCell className={isMp && !isUt ? '' : 'disabled'}>
                {String(t('mp.label.표준사양서(첨부)', '표준사양서(첨부)'))}
              </TableCell>
              <TableCell className={isMp && !isUt ? '' : 'disabled'}>
                <FileuploadBtn
                  disabled={!(isMp && !isUt)}
                  onClick={() =>
                    handleOperationAfterSave().then(() =>
                      handleFileUpload(MpItemAtchFileTpCd.SPEC_SHEET)
                    )
                  }
                >
                  <span>{(mpItem?.specSheetAtchFileCnt || 0).toLocaleString()}</span>
                </FileuploadBtn>
              </TableCell>
              */}
            </TableRow>
            <TableRow>
              <TableCell>
                <span>{String(t('mp.label.4MCC 발행번호', '4MCC 발행번호'))}</span>
              </TableCell>
              <TableCell colSpan={2}>
                <TextBtnSet>
                  {mpItem?.m4ChgNo}
                  {mpItem?.m4ChgNo && (
                    <Button
                      css={IconButton.button}
                      className="popup"
                      onClick={() => {
                        if (mpItem?.m4ChgNo) {
                          open4mcc(mpItem?.m4ChgNo);
                        }
                      }}
                    />
                  )}
                </TextBtnSet>
              </TableCell>
              <TableCell>
                <span>{String(t('mp.label.4MCC 상태', '4MCC 상태'))}</span>
              </TableCell>
              <TableCell colSpan={2}>{mpItem?.m4StatNm}</TableCell>
              <TableCell>
                <span>{String(t('mp.label.4MCC Level', '4MCC Level'))}</span>
              </TableCell>
              <TableCell colSpan={5}>
                {mpItem?.m4LvNm && mpItem?.ecoYn === 'Y' && mpItem?.m4LvNm !== 'null'
                  ? CommonUtil.unescapeHtml(mpItem?.m4LvNm)
                  : ''}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span>{String(t('mp.label.분과위원', '분과위원'))}</span>
              </TableCell>
              <TableCell colSpan={4}>
                {/* 분과위원은 MP분류, 공정에 따라 자동으로 보여 주는 기능 (수정X) */}
                <CustomInputWithSearch
                  className="find"
                  name="procMgrs"
                  placeholder={String(
                    t('mp.label.설정된 분과위원이 없습니다.', '설정된 분과위원이 없습니다.')
                  )}
                  value={procMgrNms}
                  readOnly={true}
                  onSearchClick={() => {
                    if ((procMgrs || []).length) {
                      setMcrcPopupCondition({
                        mpClsfType: mpItem?.mpClsfCd,
                        prdnProcCode: mpItem?.procCd,
                      });
                      setOpenMcrcPopup(true);
                    }
                  }}
                />
              </TableCell>
              <TableCell>
                <span className={isRequireDrw ? 'dot' : ''}>
                  {String(t('mp.label.사양서담당자', '사양서담당자'))}
                </span>
              </TableCell>
              <TableCell colSpan={2}>
                <FindInputBtnSet>
                  <CustomInputWithSearch
                    className="find"
                    name="chgCrgeUserNm"
                    placeholder={String(
                      t('mp.label.사양서담당자를 선택해 주세요.', '사양서담당자를 선택해 주세요.')
                    )}
                    value={displayCondition?.chgCrgeUserNm}
                    onChange={handleDisplayConditionChange}
                    onSearchClick={() => {
                      if (isEditable('chgCrgeUserId', mpItem?.fstrYn)) {
                        console.log('chgCrgeUserId true : ', mpItem.chgCrgeUserId);
                        setUserReadModalCondition({
                          title: t('mp.label.사양서담당자', '사양서담당자'),
                          initParam: mpItem?.chgCrgeUserId,
                        });
                        setOpenUserModal(true);
                      } else {
                        console.log('chgCrgeUserId : ', mpItem.chgCrgeUserId);
                        if (mpItem?.chgCrgeUserId) {
                          setUserReadModalCondition({
                            title: t('mp.label.사양서담당자', '사양서담당자'),
                            initParam: mpItem?.chgCrgeUserId,
                          });
                          setOpenUserReadModal(true);
                        }
                      }
                    }}
                    readOnly={!isEditable('chgCrgeUserId', mpItem?.fstrYn)}
                    isError={errors?.chgCrgeUserId}
                    msgError={String(
                      t('mp.label.사양서담당자를 선택해 주세요.', '사양서담당자를 선택해 주세요.')
                    )}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        setOpenUserModal(true);
                      }
                    }}
                  />
                  <Button
                    css={IconButton.button}
                    className="docu"
                    onClick={() => setOpenMpMgrChgHistoryPopup(true)}
                  />
                </FindInputBtnSet>
              </TableCell>
              <TableCell>
                <span className={isRequireDrw ? 'dot' : ''}>
                  {String(t('mp.label.개정대상 제작사양서', '개정대상 제작사양서'))}
                </span>
              </TableCell>
              <TableCell>
                <FindInputBtnSet>
                  <CustomInputWithSearch
                    className="find"
                    name="drwName"
                    placeholder={String(
                      t(
                        'mp.msg.개정대상 제작사양서를 선택해 주세요.',
                        '개정대상 제작사양서를 선택해 주세요.'
                      )
                    )}
                    value={(mpItem?.mpDrwList || [])
                      .reduce(
                        (acc, cur) => [...acc, CommonUtil.unescapeHtml(cur?.drwNm || '')],
                        [] as string[]
                      )
                      .filter((o) => !!o)
                      .join(', ')}
                    onSearchClick={handleOpenDrwModal}
                    readOnly={true}
                    isError={errors?.drwName}
                    msgError={String(
                      t(
                        'mp.msg.개정대상 제작사양서를 선택해 주세요.',
                        '개정대상 제작사양서를 선택해 주세요.'
                      )
                    )}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        handleOpenDrwModal();
                      }
                    }}
                  />
                  {/* // [25.08.05] FDM I/F 처리 전 임시 버튼 (추후 삭제) - s */}
                  {/* // [25.09.11] DEV, QA환경에서 ADM권한인 경우 노출되도록 수정 */}
                  {mpItem?.spshRevCtn === 'REQ' && hasIfAuth && (
                    <Button
                      css={IconButton.button}
                      className="develop"
                      onClick={() => {
                        openCommonModal({
                          modalType: 'confirm',
                          content:
                            'IF 처리 전 임시 버튼으로 모든 도면이 개정완료 처리됩니다.<br/>변경하시겠습니까?',
                          yesCallback: () => {
                            if (mpItem?.itemId) {
                              completeSpshRev(mpItem?.itemId).then((res) => {
                                openMessageBar({
                                  type: res.successOrNot === 'Y' ? 'confirm' : 'error',
                                  content:
                                    res.successOrNot === 'Y'
                                      ? '개정완료 처리되었습니다.'
                                      : res.data && typeof res.data === 'string'
                                      ? res.data
                                      : '개정완료 처리 중 오류가 발생했습니다.',
                                });
                                if (res.successOrNot === 'Y') {
                                  onRefresh();
                                }
                              });
                            }
                          },
                        });
                      }}
                    />
                  )}
                  {/* // [25.08.05] FDM I/F 처리 전 임시 버튼 (추후 삭제) - e */}
                </FindInputBtnSet>
              </TableCell>
              <TableCell>
                <span>{String(t('mp.label.제작사양서 개정 상태', '제작사양서 개정 상태'))}</span>
              </TableCell>
              <TableCell>{mpItem?.spshRevCtnNm || ''}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{String(t('mp.label.등록자', '등록자'))}</TableCell>
              <TableCell colSpan={2}>{mpItem?.dataInsUserNm}</TableCell>
              <TableCell>{String(t('mp.label.등록일자', '등록일자'))}</TableCell>
              <TableCell colSpan={2}>{dayjs(mpItem?.dataInsDtm).format('YYYY.MM.DD')}</TableCell>
              <TableCell>{String(t('mp.label.수정자', '수정자'))}</TableCell>
              <TableCell colSpan={2}>{mpItem?.dataUpdUserNm}</TableCell>
              <TableCell>{String(t('mp.label.수정일자', '수정일자'))}</TableCell>
              <TableCell colSpan={2}>{dayjs(mpItem?.dataUpdDtm).format('YYYY.MM.DD')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{String(t('mp.label.첨부미리보기', '첨부미리보기'))}</TableCell>
              <TableCell colSpan={11}>
                {groupsTitle.map((item, index) => (
                  <CustomFileImageList
                    key={index}
                    listIndex={index}
                    itemTitle={item[0].atchFileNm}
                    itemList={groupsData[item[0].key]}
                    imageCols={2}
                    imageGaps={5}
                  />
                ))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* 설비변경점 대상 설비 변경 이력 조회 */}
      {isOpenEquipmentHisModal && (
        <MpEquipmentHistoryPopUp
          open={isOpenEquipmentHisModal}
          close={() => setOpenEquipmentHisModal(false)}
          title={t(
            'mp.label.설비변경점 대상 설비 변경 이력 조회',
            '설비변경점 대상 설비 변경 이력 조회'
          )}
          condition={{
            mpItemId: mpItem?.itemId || '',
            // prdnPlntCd: mpItem?.procCd || '', // TODO 변경이력팝업 공장추가 작업 후 주석해제
            horExplYn: 'N',
          }}
        />
      )}

      {isOpenEquipmentGroupPopUp && (
        <EquipmentGroupPopUp
          open={isOpenEquipmentGroupPopUp}
          close={() => setOpenEequipmentGroupPopUp(false)}
          type={'mp'}
          condition={{
            equipmentGroup: mpItem.eqclId || '',
            copCd: mpItem.tgtCopCd || '',
          }}
          onCallback={(value) => {
            const eqclId = (value as EquipmentGroupCondition).equipmentGroup;
            setMpItem((prev) => ({
              ...prev,
              eqclId: eqclId,
            }));
          }}
        />
      )}

      {isOpenFileUploadModal && (
        <FileUploadPopUp
          open={isOpenFileUploadModal}
          close={() => setOpenFileUploadModal(false)}
          initParam={{
            atchFileGrId: fileUploadModalCondition.atchFileGrId,
            atchFileTpCd: fileUploadModalCondition.atchFileTpCd,
            optValCtn1: fileUploadModalCondition.tableName,
            bizName: fileUploadModalCondition.bizName,
          }}
          downloadOnly={!isEditable('attach')}
          onCallback={(atchFileGrId, fileCount) => {
            let key = '';
            switch (fileUploadModalCondition.atchFileTpCd) {
              case MpItemAtchFileTpCd.MP_ITEM:
                key = 'atchFileCnt';
                break;
              case MpItemAtchFileTpCd.MP_MINUTES:
                key = 'minuteAtchFileCnt';
                break;
              case MpItemAtchFileTpCd.SPEC_SHEET:
                key = 'specSheetAtchFileCnt';
                break;
              case MpItemAtchFileTpCd.MP_ITEM_RPT:
                key = 'mpItemRptAtchFileCnt';
                break;
              case MpItemAtchFileTpCd.VAID_EVL_RPT:
                key = 'vaidEvlRptAtchFileCnt';
                break;
              case MpItemAtchFileTpCd.SCMM_MINT:
                key = 'scmmMintAtchFileCnt';
                break;
              case MpItemAtchFileTpCd.MP_FSTR:
                key = 'mpFstrAtchFileCnt';
                break;
              case MpItemAtchFileTpCd.MP_HE_RPT:
                key = 'mpHeRptAtchFileCnt';
                break;
            }
            setMpItem((prev) => ({
              ...prev,
              [key]: fileCount,
            }));
            setOpenFileUploadModal(false);

            // 미리보기 이미지가 있는지 조회
            getPreviewImage();
          }}
        />
      )}

      {/* 대상 설비 선택 */}
      {isOpenEquipmentModal && (
        <EquipmentPopUp
          open={isOpenEquipmentModal}
          close={() => setOpenEquipmentModal(false)}
          title={t('mp.label.설비추가', '설비추가')}
          condition={{
            // copCds: [mpItem?.tgtCopCd || ''],
            copCd: mpItem?.tgtCopCd,
            // processCode: mpItem?.procCd, //25.11.10 조건 전체로 설정을 위해 주석처리 (PI 요청)
            equipmentGroup: mpItem?.eqclId,
            prdnPlntCd: mpItem?.tgtPlntCd,
            chgRsnCtn: mpItem?.eqpChgRsnCtn,
            equipmentLevel: `${EquipmentLevel.MACHINE},${EquipmentLevel.MAIN}`, // [25.09.01] mp 설비대상선택시 기본값 머신으로 변경(사용자테스트 후 개선요청)
            equipmentLevels: [EquipmentLevel.MACHINE, EquipmentLevel.MAIN], // CSR E20251027-000124 MAIN 추가요청
          }}
          isMpItem={true}
          isCopCdFix={true}
          isProcCdFix={false}
          isEqpGrFix={true}
          isPrdnPlntFix={true}
          // isFixedMainLevel={true} isFixedMainLevel={true} // [25.07.28] 설비추가 시 설비레벨 M,C,U까지 가능
          isChgRsnCtn={
            isEditable('equipmentName') &&
            !(
              MpItemTpCd.NI === mpItem?.mpItemTpCd &&
              (!mpItem?.itemStatCd || ItemStatCd.Develop === mpItem?.itemStatCd)
            )
          }
          singleSelect={false}
          editable={false}
          defaultEquipment={(mpItem?.resultList || []).reduce(
            (acc, cur) => [
              ...acc,
              {
                ...cur,
                equipmentId: cur.eqpId,
                equipmentName: cur.equipmentName,
                cortCrgeUserId: cur.cortCrgeUserId,
                cortCrgeUserNm: cur.cortCrgeUserNm,
                equipmentGroup: cur.equipmentGroup,
                equipmentDesc: cur.equipmentName,
                copCd: cur.copCd,
                prdnPlntCd: cur.plntCd,
                prdnPlntNm: cur.plntNm,
                factoryCode: cur.factoryCode,
                processCode: cur.processCode,
                chgRsnCtn: cur.chgRsnCtn,
              },
            ],
            [] as CustomEquipment[]
          )}
          onCallback={(result) => {
            const equipments = (result?.eqpList || []) as CustomEquipment[];
            setMpItem((prev) => ({
              ...prev,
              eqpChgRsnCtn: result?.remark,
              resultList: equipments.reduce(
                (acc, cur) => [
                  ...acc,
                  {
                    ...cur,
                    eqpId: cur.equipmentId,
                    equipmentName: cur.equipmentDesc,
                    equipmentDesc: cur.equipmentDesc, //설비명 > 설명으로 변경됨 명칭은 설비명이 맞음.
                    cortCrgeUserId: cur.cortCrgeUserId,
                    cortCrgeUserNm: cur.cortCrgeUserNm,
                    equipmentGroup: cur.equipmentGroup,
                    copCd: cur.copCd,
                    plntCd: cur.prdnPlntCd,
                    plntNm: cur.prdnPlntNm,
                    factoryCode: cur.factoryCode,
                    processCode: cur.processCode,
                    useYn: CommonYN.Y,
                    chgRsnCtn: result?.remark,
                  },
                ],
                [] as MpResult[]
              ),
            }));
            setErrors((prev) => ({ ...prev, equipmentName: false }));
          }}
        />
      )}

      {/* 대상 설비 목록 (readonly) */}
      {isOpenMpItemEqpListModal && mpItem?.itemId && (
        <MpEquipmentPopUp
          open={isOpenMpItemEqpListModal}
          close={() => setOpenMpItemEqpListModal(false)}
          condition={{ mpItemId: mpItem?.itemId || '', horExplYn: 'N' }}
          title={t('mp.label.대상설비목록', '대상설비목록')}
        />
      )}

      {isOpenUserModal && (
        <UserMultiModal
          open={isOpenUserModal}
          close={() => setOpenUserModal(false)}
          title={t('com.label.담당자 조회', '담당자 조회')}
          singleSelect={true}
          isOnlyUsed={true}
          defaultUserId={mpItem?.chgCrgeUserId}
          condition={{
            userId: mpItem?.chgCrgeUserId || '',
            empNm: displayCondition?.chgCrgeUserNm || '',
            rmk: mpItem?.drwMgrChgRsnCtn,
          }}
          onCallback={(result: any) => {
            setMpItem((prev) => ({
              ...prev,
              chgCrgeUserId: result?.userId,
              drwMgrChgRsnCtn: result?.rmk,
            }));

            console.log('사양서담당자  id : ', mpItem?.chgCrgeUserId);

            setErrors((prev) => ({
              ...prev,
              chgCrgeUserId: false,
            }));
            setDisplayCondition((prev) => ({
              ...prev,
              chgCrgeUserNm: result?.empNm || '',
            }));
          }}
        />
      )}

      {isOpenUserReadModal && (
        <GatingContListPopUp
          open={isOpenUserReadModal}
          close={() => setOpenUserReadModal(false)}
          title={userReadModalCondition?.title}
          initParam={userReadModalCondition?.initParam}
        />
      )}

      {isOpenMcrcPopup && mcrcPopupCondition?.mpClsfType && mcrcPopupCondition?.prdnProcCode && (
        <MpMcrcPopUp
          open={isOpenMcrcPopup}
          close={() => setOpenMcrcPopup(false)}
          condition={{
            mpClsfType: mcrcPopupCondition?.mpClsfType,
            prdnProcCode: mcrcPopupCondition?.prdnProcCode,
          }}
        />
      )}

      {isOpenDrwPopup && (
        <MpDrwPopUp
          open={isOpenDrwPopup}
          close={() => setOpenDrwPopup(false)}
          condition={{}}
          selectedIds={drwPopupCondition?.drwIds}
          onCallbackData={(result: DrwList[]) => {
            //25.11.12 설비변경점 개선사항 v5.2 제작사양서 해당사항없음 선태 시 FDM 연계없이 완료 처리
            // drwId === '1' 은 제작사양서 해당사항없음
            // drwCpltY로 해당사항없음 여부 추가하여 백단에서 FDM 연계여부 확인
            const selectedCpltDrw = result.filter((item) => item?.drwId === '1');
            if (selectedCpltDrw.length > 0) {
              setMpItem((prev) => ({ ...prev, mpDrwList: selectedCpltDrw, drwCpltY: 'Y' }));
            } else {
              setMpItem((prev) => ({ ...prev, mpDrwList: result, drwCpltY: 'N' }));
            }

            if ((result || []).length > 0) {
              setErrors((prev) => ({ ...prev, drwName: false }));
            }
          }}
        />
      )}

      {isOpenDrwRetrievePopUp && (
        <MpDrwRetrievePopUp
          open={isOpenDrwRetrievePopUp}
          close={() => setOpenDrwRetrievePopUp(false)}
          condition={{
            mpItemId: defaultMpItem?.itemId,
          }}
          onCallbackData={null}
        />
      )}

      {isOpenMpMgrChgHistoryPopup && defaultMpItem?.itemId && (
        <MpMgrChgHistoryPopUp
          open={isOpenMpMgrChgHistoryPopup}
          close={() => setOpenMpMgrChgHistoryPopup(false)}
          title={String(t('mp.label.사양서담당자', '사양서담당자'))}
          condition={{
            mpItemId: defaultMpItem?.itemId,
            mgrTpCd: 'DRWR',
          }}
        />
      )}
    </>
  );
};

export default React.forwardRef(MpItemInfoContent);
